import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const modelMount = document.querySelector('.cwf-plc .three-model');

if (modelMount) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 100);
  camera.position.set(0, 0.34, 4.2);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  modelMount.appendChild(renderer.domElement);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

  scene.add(new THREE.HemisphereLight(0xeaf3ff, 0x101823, 2.35));

  const key = new THREE.DirectionalLight(0xffffff, 3.25);
  key.position.set(3.4, 4.2, 4.6);
  key.castShadow = true;
  scene.add(key);

  const rim = new THREE.DirectionalLight(0xaec9ff, 2.2);
  rim.position.set(-3.2, 1.8, -2.8);
  scene.add(rim);

  const lowFill = new THREE.DirectionalLight(0x8da8c9, 0.85);
  lowFill.position.set(0, -2.4, 2.4);
  scene.add(lowFill);

  const materials = [
    new THREE.MeshPhysicalMaterial({
      color: 0x596878,
      roughness: 0.48,
      metalness: 0.72,
      clearcoat: 0.38,
      clearcoatRoughness: 0.44,
      emissive: 0x0b1118,
      emissiveIntensity: 0.16,
      envMapIntensity: 1.1
    }),
    new THREE.MeshPhysicalMaterial({
      color: 0x9aa8b8,
      roughness: 0.31,
      metalness: 0.86,
      clearcoat: 0.24,
      clearcoatRoughness: 0.34,
      emissive: 0x0a111a,
      emissiveIntensity: 0.08,
      envMapIntensity: 1.22
    }),
    new THREE.MeshPhysicalMaterial({
      color: 0x2d3744,
      roughness: 0.62,
      metalness: 0.78,
      clearcoat: 0.2,
      clearcoatRoughness: 0.62,
      emissive: 0x101a25,
      emissiveIntensity: 0.24,
      envMapIntensity: 0.82
    }),
    new THREE.MeshPhysicalMaterial({
      color: 0xdfeeff,
      roughness: 0.08,
      metalness: 0.15,
      transmission: 0.2,
      thickness: 0.15,
      transparent: true,
      opacity: 0.72,
      emissive: 0x9fc4ff,
      emissiveIntensity: 0.12,
      envMapIntensity: 1.45
    })
  ];

  const edgeMaterial = new THREE.LineBasicMaterial({
    color: 0xdcecff,
    transparent: true,
    opacity: 0.16,
    depthWrite: false
  });

  const deck = new THREE.Group();
  const grid = new THREE.GridHelper(3.7, 18, 0x5d728a, 0x1c2a38);
  grid.material.transparent = true;
  grid.material.opacity = 0.18;
  grid.position.y = -1.08;
  deck.add(grid);

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(1.18, 1.22, 96),
    new THREE.MeshBasicMaterial({ color: 0xbfd6f4, transparent: true, opacity: 0.18, side: THREE.DoubleSide })
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = -1.075;
  deck.add(ring);
  scene.add(deck);

  let model = null;
  let baseRotation = null;
  let cameraDistance = 4.2;
  let minCameraDistance = 2.2;
  let maxCameraDistance = 7.2;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let autoSpin = !prefersReducedMotion;
  let autoSpinResumeAt = 0;
  let lastFrameTime = performance.now();
  let floatPhase = Math.random() * Math.PI * 2;
  const rotationVelocity = new THREE.Vector2(0, 0);
  let pointerState = null;

  function updateCamera() {
    camera.position.set(0, cameraDistance * 0.1, cameraDistance);
    camera.lookAt(0, 0, 0);
  }

  function showModelError() {
    const error = document.createElement('div');
    error.className = 'model-error';
    error.textContent = '3D model unavailable';
    modelMount.appendChild(error);
  }

  function collectMeshes(root) {
    const meshes = [];
    root.traverse((obj) => {
      if (obj.isMesh && obj.geometry) meshes.push(obj);
    });
    return meshes;
  }

  function styleMesh(mesh, index, total) {
    const materialIndex = total > 2 ? index % materials.length : index === 0 ? 0 : 1;
    mesh.material = materials[materialIndex].clone();
    mesh.material.side = THREE.DoubleSide;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    if (!mesh.geometry.attributes.normal) mesh.geometry.computeVertexNormals();

    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry, 34), edgeMaterial.clone());
    edges.renderOrder = 2;
    mesh.add(edges);
  }

  function meshBounds(meshes) {
    const box = new THREE.Box3();
    meshes.forEach((mesh) => {
      mesh.updateMatrixWorld(true);
      box.expandByObject(mesh);
    });
    return box;
  }

  function fitModel(pivot, content, meshes) {
    const box = meshBounds(meshes);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    // The CAD origin is not at the visible center of the light. Offset the
    // imported contents inside a clean pivot, then rotate/scale the pivot.
    content.position.sub(center);

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    pivot.scale.setScalar(2.35 / maxDim);

    const longestAxis = size.y > size.x && size.y >= size.z ? 'y' : size.z > size.x && size.z >= size.y ? 'z' : 'x';
    if (longestAxis === 'y') pivot.rotation.z = -Math.PI * 0.5;
    if (longestAxis === 'z') pivot.rotation.y = Math.PI * 0.5;

    pivot.rotation.x += -0.24;
    pivot.rotation.y += -0.58;
    pivot.rotation.z += 0.025;
    baseRotation = pivot.rotation.clone();

    const fittedBox = new THREE.Box3().setFromObject(pivot);
    const sphere = fittedBox.getBoundingSphere(new THREE.Sphere());
    cameraDistance = sphere.radius * 3.25;
    minCameraDistance = sphere.radius * 1.65;
    maxCameraDistance = sphere.radius * 5.4;
    updateCamera();
  }

  function resetModelView() {
    if (!model || !baseRotation) return;
    model.rotation.copy(baseRotation);
    rotationVelocity.set(0, 0);
    autoSpin = !prefersReducedMotion;
    autoSpinResumeAt = 0;
    cameraDistance = Math.min(Math.max(cameraDistance, minCameraDistance), maxCameraDistance);
    updateCamera();
  }

  const loader = new GLTFLoader();
  loader.load(
    modelMount.dataset.modelSrc,
    (gltf) => {
      const pivot = new THREE.Group();
      pivot.name = 'PL-C CAD manipulation pivot';
      model = pivot;

      const meshes = collectMeshes(gltf.scene);
      meshes.forEach((mesh, index) => styleMesh(mesh, index, meshes.length));

      pivot.add(gltf.scene);
      scene.add(pivot);
      fitModel(pivot, gltf.scene, meshes);
    },
    undefined,
    showModelError
  );

  function resizeModel() {
    const rect = modelMount.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  const resizeObserver = new ResizeObserver(resizeModel);
  resizeObserver.observe(modelMount);
  resizeModel();
  updateCamera();

  renderer.domElement.addEventListener('pointerdown', (event) => {
    if (!model) return;
    renderer.domElement.setPointerCapture(event.pointerId);
    autoSpin = false;
    autoSpinResumeAt = 0;
    rotationVelocity.set(0, 0);
    pointerState = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      rotation: model.rotation.clone()
    };
    renderer.domElement.style.cursor = 'grabbing';
  });

  renderer.domElement.addEventListener('pointermove', (event) => {
    renderer.domElement.style.cursor = pointerState ? 'grabbing' : 'grab';
    if (!model || !pointerState || pointerState.id !== event.pointerId) return;

    const dx = event.clientX - pointerState.x;
    const dy = event.clientY - pointerState.y;
    model.rotation.y = pointerState.rotation.y + dx * 0.008;
    model.rotation.x = THREE.MathUtils.clamp(pointerState.rotation.x + dy * 0.008, -Math.PI * 0.92, Math.PI * 0.92);
    rotationVelocity.set(
      THREE.MathUtils.clamp((event.clientX - pointerState.lastX) * 0.0016, -0.022, 0.022),
      THREE.MathUtils.clamp((event.clientY - pointerState.lastY) * 0.0016, -0.018, 0.018)
    );
    pointerState.lastX = event.clientX;
    pointerState.lastY = event.clientY;
  });

  function releasePointer(event) {
    if (pointerState && pointerState.id === event.pointerId) {
      pointerState = null;
      autoSpinResumeAt = performance.now() + 1800;
      renderer.domElement.style.cursor = 'grab';
    }
  }

  renderer.domElement.addEventListener('pointerup', releasePointer);
  renderer.domElement.addEventListener('pointercancel', releasePointer);

  renderer.domElement.addEventListener('wheel', (event) => {
    event.preventDefault();
    autoSpin = false;
    autoSpinResumeAt = performance.now() + 1400;
    cameraDistance = THREE.MathUtils.clamp(cameraDistance + event.deltaY * 0.003, minCameraDistance, maxCameraDistance);
    updateCamera();
  }, { passive: false });

  renderer.domElement.addEventListener('dblclick', () => {
    resetModelView();
  });

  function animate(now) {
    requestAnimationFrame(animate);
    const deltaSeconds = Math.min((now - lastFrameTime) / 1000, 0.05);
    lastFrameTime = now;

    if (model && !pointerState) {
      if (rotationVelocity.lengthSq() > 0.000001) {
        model.rotation.y += rotationVelocity.x;
        model.rotation.x = THREE.MathUtils.clamp(model.rotation.x + rotationVelocity.y, -Math.PI * 0.92, Math.PI * 0.92);
        rotationVelocity.multiplyScalar(Math.pow(0.9, deltaSeconds * 60));
      }

      if (!autoSpin && autoSpinResumeAt && now > autoSpinResumeAt) {
        autoSpin = !prefersReducedMotion;
        autoSpinResumeAt = 0;
      }

      if (autoSpin) {
        floatPhase += deltaSeconds * 0.95;
        model.rotation.y += deltaSeconds * 0.17;
        model.rotation.x += Math.sin(floatPhase) * 0.00045;
        model.rotation.z = (baseRotation?.z || 0) + Math.sin(floatPhase * 0.74) * 0.035;
        model.position.y = Math.sin(floatPhase * 1.1) * 0.035;
      }
    } else if (model) {
      model.position.y *= 0.92;
    }

    if (model) {
      deck.rotation.y += deltaSeconds * 0.11;
    }

    renderer.render(scene, camera);
  }

  requestAnimationFrame(animate);
}
